import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Account } from '../types/account';
import { accountService } from '../services/api';
import AccountCard from './AccountCard';

interface PartnerAccountsProps {
  partnerId: string;
  partnerType?: 'MERCHANT' | 'RETAILER' | 'AGENT';
}

const PartnerAccounts: React.FC<PartnerAccountsProps> = ({ partnerId, partnerType }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountService.getPartnerAccounts(partnerId);
      setAccounts(data);
    } catch (err: any) {
      console.error('Error loading partner accounts:', err);
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, [partnerId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={loadAccounts} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (accounts.length === 0) {
    if (partnerType === 'AGENT') {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Agents do not have wallet accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agent float is managed in the external float system. Collections are recorded
            against the shared Agent Pool account, attributed by agent code and voucher reference.
          </Typography>
        </Box>
      );
    }
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No accounts found for this partner
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Accounts should be automatically created when a partner is created.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAccounts}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Partner Accounts</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadAccounts}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {accounts.map((account) => (
          <Grid item xs={12} md={6} key={account.id}>
            <AccountCard
              account={account}
              showPartnerInfo={false}
              onClick={() => navigate(`/accounts/${account.accountNumber}`)}
            />
          </Grid>
        ))}
      </Grid>

      {accounts.length > 0 && (
        <Box mt={3}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Total Accounts:</strong> {accounts.length} |{' '}
              <strong>Total Balance (USD):</strong>{' '}
              {accounts
                .filter((a) => a.currency === 'USD')
                .reduce((sum, a) => sum + a.currentBalance, 0)
                .toFixed(2)}{' '}
              |{' '}
              <strong>Total Balance (ZWG):</strong>{' '}
              {accounts
                .filter((a) => a.currency === 'ZWG')
                .reduce((sum, a) => sum + a.currentBalance, 0)
                .toFixed(2)}
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PartnerAccounts;
